/**
 * React surface for the ERC-7715 signing path used by MetaMask-managed
 * accounts.
 *
 * Why this lives in its own hook
 * ──────────────────────────────
 * The 7710-direct path (`useDelegationSigner.signDelegationTypedData`)
 * uses `eth_signTypedData_v4` against an MDF Delegation EIP-712
 * payload. MetaMask intentionally rejects that method for delegation
 * payloads via `validateDelegation` in
 * `@metamask/core/signature-controller/utils/validation.ts`. The
 * intended path for MM is ERC-7715, which goes through a completely
 * different JSON-RPC method and different request/response shapes.
 *
 * Method name — `wallet_requestExecutionPermissions` (current) vs
 * `wallet_grantPermissions` (legacy)
 * ────────────────────────────────────────────────────────────────
 * ERC-7715 originally defined the method as `wallet_grantPermissions`.
 * It was renamed mid-2025 to `wallet_requestExecutionPermissions`
 * (see ethereum/ERCs commit 2adc378 and MetaMask/metamask-extension
 * PR #39176 "feat: align with updated 7715 standard"). Current MM
 * builds — every Flask shipping the gator-permissions-controller@^1 —
 * only register the NEW name. Older Flask 13.5.x–13.8.x builds shipped
 * the original name.
 *
 * Both names accept the same `[PermissionRequest[]]` params shape
 * (the rename was a request-method rename; the per-permission schema
 * was already aligned in `@metamask/7715-permission-types@>=0.7`).
 * We dispatch the new name first; if the wallet returns -32601
 * (method not found) we retry under the legacy name. The fallback is
 * silent to the user (MM never prompts for an unsupported method —
 * the rejection is immediate at the middleware layer) so the cost
 * of trying both is one extra in-process RPC.
 *
 * Mixing both into `useDelegationSigner` would force every consumer
 * to know which path it's on. Splitting them keeps each hook focused
 * and makes the dispatch live in one place — `useEnableDepositProtection`,
 * which already reads the wallet capability surface to choose.
 *
 * Surface
 * ───────
 *   - `useGrantProtectionPermissions()`
 *       returns `{ grant, isGranting }`. `grant(args)` builds the
 *       request payload via `buildGrantPermissionsRequest`, calls
 *       `wallet_requestExecutionPermissions` (falling back to the
 *       legacy `wallet_grantPermissions` on -32601), decodes the
 *       response, and returns one normalised `DecodedPermissionGrant`
 *       per requested token. The hook never touches our DB or the
 *       enable-mutation — that lives in `useEnableDepositProtection`.
 *
 * Error handling
 * ──────────────
 * The MM extension returns EIP-1193-style errors for the common
 * paths:
 *   - `4001` user rejected
 *   - `-32601` method not supported on BOTH names (genuinely no
 *      7715 support — wallet is not MM Flask, or MM stable without
 *      gator-permissions support, or MM Flask too old/too new)
 *   - `4200` unsupported method (newer wallets)
 *   - `5710` (extension-specific) / text-matched "user must upgrade
 *      to a Smart Account first" — surfaced by MM when the active
 *      account hasn't been upgraded to MM Smart Account yet
 *
 * We rewrite each into actionable copy. Anything else passes through
 * untouched so we don't lose the original signal in observability.
 */

'use client';

import { useMemo, useState } from 'react';
import { useWallets } from '@privy-io/react-auth';
import type { Hex } from 'viem';
import { useWallet } from './use-wallet';
import {
  type BuildGrantPermissionsArgs,
  type DecodedPermissionGrant,
  type ProtectionPermissionRequest,
  buildGrantPermissionsRequest,
  decodeGrantPermissionsResponse,
  isProviderError,
} from '@/lib/protection/grant-permissions';

/**
 * Canonical 7715 method (post-rename, current MM builds).
 *
 * Exported only for the hook's tests — production callers go through
 * {@link requestPermissionsRpc} which handles the fallback chain.
 */
export const WALLET_PERMISSIONS_METHOD = 'wallet_requestExecutionPermissions' as const;

/**
 * Legacy 7715 method name. Older MetaMask Flask builds (13.5.x–13.8.x)
 * registered the original draft name; the rename to
 * `wallet_requestExecutionPermissions` shipped in MM extension PR
 * #39176 alongside `@metamask/gator-permissions-controller@^1.0.0`.
 * Kept as a fallback so users on the older Flask line still work.
 */
export const WALLET_PERMISSIONS_METHOD_LEGACY = 'wallet_grantPermissions' as const;

interface ProviderRequestShape {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
}

/**
 * Dispatch the 7715 RPC against the wallet, trying the canonical
 * name first and falling back to the legacy name iff the wallet
 * reports "method not found" / "unsupported" for the canonical one.
 *
 * Why retry only on `-32601` / `4200`:
 *   - User-rejection (4001), Smart-Account-upgrade-required, and
 *     "invalid params" errors should NOT trigger a fallback — they
 *     mean the wallet processed the request and made a decision.
 *     Retrying the legacy name would either re-prompt the user
 *     (annoying) or silently double-emit the same error.
 *   - `-32601` / `4200` mean the middleware never reached the
 *     permissions handler — the method name itself wasn't routed.
 *     Retrying with the alternate name is safe (no user-visible
 *     prompt was shown) and the only way to recover for older Flask.
 *
 * Params shape — flat for the canonical name, wrapped for legacy:
 *   The original ERC-7715 draft defined `params: [PermissionRequest[]]`
 *   (a single-element wrapper array). Ethereum/ERCs commit 2adc378
 *   (the same patch that renamed the method) simplified the shape
 *   to `params: PermissionRequest[]` — the request array IS params.
 *   MM ships the new flat shape under the new name (gator-
 *   permissions-controller@^1) and the old wrapped shape on builds
 *   that still expose only `wallet_grantPermissions`. We mirror that
 *   split so each branch matches the spec version it targets — a
 *   wrapped payload to the new name fails MM's struct validator with
 *   "0 > chainId — Expected a string, but received: undefined" (MM
 *   tries to read `params[0].chainId` and finds an array there).
 *
 * The function is exported so the hook's tests can pin both branches.
 */
export async function requestPermissionsRpc(
  provider: ProviderRequestShape,
  requests: ProtectionPermissionRequest[],
): Promise<unknown> {
  try {
    // Flat shape: post-rename spec — `params` IS the request array.
    return await provider.request({
      method: WALLET_PERMISSIONS_METHOD,
      params: requests,
    });
  } catch (err: unknown) {
    if (
      isProviderError(err) &&
      (err.code === -32601 || err.code === 4200)
    ) {
      // Wrapped shape: legacy spec — `params: [PermissionRequest[]]`.
      // Try the legacy method name once before surfacing the error.
      // This catches users still on MM Flask <13.9.0 (pre-rename).
      return await provider.request({
        method: WALLET_PERMISSIONS_METHOD_LEGACY,
        params: [requests],
      });
    }
    throw err;
  }
}

/**
 * Translate provider-side errors thrown during `wallet_grantPermissions`
 * into actionable copy.
 *
 * Goals:
 *   - User-rejection paths produce a *short* message so the toast
 *     doesn't read like a stack trace.
 *   - "Method not supported" surfaces as "your MetaMask is too old"
 *     guidance with a link to the SCA-upgrade flow.
 *   - "Account must be upgraded to Smart Account" surfaces a clear
 *     callout — that's a one-time MM action the user has to take in
 *     their wallet UI; we can't trigger it for them.
 *   - Everything else passes through to preserve the original signal.
 */
export function normalizeGrantPermissionsError(err: unknown): Error {
  const original =
    err instanceof Error
      ? err
      : new Error(typeof err === 'string' ? err : 'Permission request failed');

  if (isProviderError(err)) {
    if (err.code === 4001) {
      return new Error('Permission request cancelled');
    }
    if (err.code === -32601 || err.code === 4200) {
      return new Error(
        'Your wallet does not support ERC-7715 yet. Update MetaMask, or reconnect ' +
          'with Coinbase Wallet, Rabby, or Frame to enable Land Protection.',
      );
    }
  }

  // MM's smart-account bridge surfaces an account-upgrade prompt as a
  // descriptive message rather than a stable error code. Match the
  // string MM ships in the public extension build. We pull from the
  // raw `err` (which may be a plain `{ code, message }` object) AND
  // the wrapped `original` — when MM throws a plain object, the
  // wrapper drops the original message into a generic "Permission
  // request failed" string, so falling back to `err.message` here is
  // load-bearing.
  const surfaceText = [
    original.message,
    (err as { message?: unknown } | null)?.message,
    (err as { data?: { message?: string } } | null)?.data?.message,
    (err as { details?: string } | null)?.details,
  ]
    .filter((s): s is string => typeof s === 'string')
    .join(' \u00B7 ');

  if (/upgrade.*smart account|switch to a smart account/i.test(surfaceText)) {
    return new Error(
      'MetaMask requires your account to be upgraded to a Smart Account before granting ' +
        'Land Protection. Open MetaMask, switch the account to a Smart Account, ' +
        'then try again.',
    );
  }

  return original;
}

interface UseGrantProtectionPermissionsResult {
  /**
   * Trigger MetaMask's `wallet_grantPermissions` flow. Returns the
   * decoded grants in the same order as the requested permissions
   * (one per token with a cap > 0; USDC first, then TAKEOVER, when
   * both are present).
   *
   * Throws if the active wallet provider isn't reachable, the wallet
   * rejects the request, or the response fails decoding. Callers
   * should `await` this from inside a `useMutation` so the failure
   * surfaces via React Query's `onError`.
   */
  grant: (args: GrantInput) => Promise<DecodedPermissionGrant[]>;
  isGranting: boolean;
}

/**
 * Public input shape — we hide the raw `from`/`to` field names from
 * the consumer (the hook fills `from` from the active wallet) and
 * relabel `sessionAccount` → `keeperAddress` for clarity.
 */
export interface GrantInput
  extends Omit<BuildGrantPermissionsArgs, 'fromAccount' | 'sessionAccount'> {
  /**
   * Address the granted permission delegates TO. Today this is the
   * `ProtectionRedeemer` contract address (pinned via
   * `NEXT_PUBLIC_PROTECTION_REDEEMER_ADDRESS`). The contract validates
   * each redemption batch (seat owner, UGM allowlist, runway) before
   * forwarding to the DelegationManager. The field name keeps the
   * "keeper" terminology because that's still how the server-side
   * schema labels it — see `use-deposit-protection.ts` for the
   * compatibility note.
   */
  keeperAddress: BuildGrantPermissionsArgs['sessionAccount'];
}

export function useGrantProtectionPermissions(): UseGrantProtectionPermissionsResult {
  const { address } = useWallet();
  const { wallets } = useWallets();
  const [isGranting, setIsGranting] = useState(false);

  const active = useMemo(() => {
    if (!address) return null;
    return wallets.find((w) => w.address?.toLowerCase() === address.toLowerCase()) ?? null;
  }, [wallets, address]);

  const grant = async (args: GrantInput): Promise<DecodedPermissionGrant[]> => {
    if (!active || !address) {
      throw new Error('useGrantProtectionPermissions: no active wallet');
    }
    const provider = await active.getEthereumProvider();

    const requests = buildGrantPermissionsRequest({
      ...args,
      fromAccount: address as Hex,
      sessionAccount: args.keeperAddress,
    });

    setIsGranting(true);
    try {
      // ERC-7715 spec: params is `[PermissionRequest[]]` — i.e., the
      // RPC takes a single array argument. Some wallets accept the
      // array directly without the wrapping (looser parsing), but
      // we follow the spec form so the official MM extension and
      // permission-types library agree on the shape.
      //
      // Method name: dispatch the post-rename canonical name first
      // (`wallet_requestExecutionPermissions`), fall back to the
      // legacy name (`wallet_grantPermissions`) only if the wallet
      // reports the new name as unsupported. See the file-level
      // comment for the rationale and the MetaMask PR reference.
      const raw = await requestPermissionsRpc(provider, requests);
      return decodeGrantPermissionsResponse(raw, {
        expected: {
          usdc: args.weeklyUsdcCap > 0n ? args.usdcAddress : undefined,
          takeover: args.weeklyTakeoverCap > 0n ? args.takeoverAddress : undefined,
        },
      });
    } catch (err: unknown) {
      throw normalizeGrantPermissionsError(err);
    } finally {
      setIsGranting(false);
    }
  };

  return { grant, isGranting };
}
